export const paths = {
  root: '/',
  login: '/login',
  changePassword: '/change-password',
  dashboard: '/dashboard',
  /**
   * The Dashboard module — live trading health across the branches.
   *
   * Not `/dashboard`, which this app has always used for HOME: the screen the
   * logo marks, with the greeting and the back-office catalogue on it. The two
   * are different places — home is where you choose a module, this is one of
   * them — and the app bar tells them apart by exactly this path.
   */
  overview: '/overview',
  products: '/products',
  /** Catalogue structure: how products are grouped, varied and discounted. */
  categories: '/categories',
  modifiers: '/modifiers',
  discounts: '/discounts',
  /**
   * Inventory — what the business counts, and how much of it there is.
   *
   * Its own module beside `/products` rather than a tab inside it: a stock item
   * is not a catalogue product. The milk and cups behind a latte are counted here
   * and never sold on their own, and the two live on different services.
   */
  inventory: '/inventory',
  /** Inventory's other tabs, each its own route so a link or a reload lands on it. */
  inventoryRecipes: '/inventory/recipes',
  /**
   * One recipe's editor — a page of its own rather than a dialog over the list,
   * so a long ingredient list has room and a link or reload lands back on it.
   */
  inventoryRecipe: '/inventory/recipes/:recipeId',
  /**
   * A recipe for an item that has none. Carries `?item=`. Rare: an item made
   * from other items is created with an empty draft, which opens at the route
   * above instead. A static segment, so it outranks `:recipeId`.
   */
  inventoryRecipeNew: '/inventory/recipes/new',
  inventoryOverview: '/inventory/overview',
  inventoryAdjustments: '/inventory/adjustments',
  inventoryCounts: '/inventory/counts',
  inventoryModifiers: '/inventory/modifiers',
  inventoryUnits: '/inventory/units',
  inventoryLocations: '/inventory/locations',
  orders: '/orders',
  /**
   * Customers — the shoppers who granted this store permission.
   *
   * Beside Orders rather than inside it: the two answer different questions.
   * Orders is "what came in today"; this is "who buys from us", and a customer
   * who has granted permission without ordering yet only exists on one of them.
   */
  customers: '/customers',
  /** QR ordering — the dining room, as scannable codes. */
  tables: '/tables',
  /**
   * Point of Sale — registers, floor and cash oversight.
   *
   * Its own module beside `/tables` rather than inside it. The two share the
   * table rows, but they answer different questions: `/tables` prints the codes
   * a customer scans, `/pos` watches what the tills are doing with them.
   */
  pos: '/pos',
  /** The shopfront window: banners and the standing content pages. */
  storefront: '/storefront',
  /** The business itself, and the branches it trades from. */
  business: '/business',
  /** Opening hours and delivery zones — the rate cards the server seeds. */
  operations: '/operations',
  payments: '/payments',
  /**
   * Reports — the documents a manager opens to read, not to change.
   *
   * Its own module rather than a tab inside `/pos`, because the errand IS the
   * report: "take an X", "reprint last night's Z". A drawer's figures also show
   * against its row in `/pos`, where the question is about that row instead.
   */
  reports: '/reports',
  /**
   * One report, by its slug — `/reports/stock-on-hand`. Each has its own url so
   * it can be bookmarked and sent; the slugs are in `report-catalogue.ts`.
   */
  report: '/reports/:report',
  /** The module's one route: the landing, or a report when the segment is there. */
  reportsModule: '/reports/:report?',
  staff: '/staff',
  /** Where the invitation email lands. Carries `?token=`. */
  acceptInvitation: '/staff/invitations/accept',
  /** Where ABA hands the merchant back once they submit the onboarding form. */
  paymentReturn: '/payments/:referentId',
  /**
   * The same page, one segment deeper. The API builds the return url itself and always
   * ends it `/completed` — see `partner.service.ts`, which ignores the `redirectUrl`
   * sent with the registration — so the segment is a constant, not a verdict. The route
   * matches with and without it; the outcome comes off the registration status.
   */
  paymentReturnOutcome: '/payments/:referentId/:outcome',
  notFound: '*',
} as const

/** The editor of one recipe. */
export function inventoryRecipePath(recipeId: string) {
  return paths.inventoryRecipe.replace(':recipeId', encodeURIComponent(recipeId))
}

/** The editor for a new recipe, for the item it will make. */
export function inventoryNewRecipePath(itemId: string) {
  return `${paths.inventoryRecipeNew}?${new URLSearchParams({ item: itemId }).toString()}`
}

/**
 * The concrete return url for one registration. ABA is given this before the merchant
 * ever leaves, so the reference has to be known up front — see `startAbaRegistration`,
 * which mints it rather than letting the server generate one.
 */
export function paymentReturnPath(referentId: string) {
  return paths.paymentReturn.replace(':referentId', encodeURIComponent(referentId))
}

/** The url of one report inside Reports. */
export function reportPath(report: string) {
  return paths.report.replace(':report', encodeURIComponent(report))
}
